/* eslint-disable no-restricted-properties --
 * dataClient is a TRANSPORT, a peer of apiBridge.ts, not one of its callers.
 *
 * The rule exists so a CALL SITE cannot invent its own answer to "did the
 * backend answer, or should I fall back to Supabase?" — apiBridge owns that
 * decision. This file is the other side of it: it is the thing that REPLACED
 * Supabase for `supabase.from(...)`, so there is no fallback for it to get
 * wrong, and routing it through apiBridge would be circular (apiBridge is built
 * on apiClient, and every bridged call would re-enter this file).
 *
 * Exempted HERE, at the file, rather than by widening either override list in
 * package.json: `apiBridgeGuard.test.ts` pins the door list to exactly
 * apiBridge.ts and caps the burn-down list at its current 33, and both of those
 * guards are doing their job. This does not weaken the rule for anything else.
 */
/**
 * dataClient.ts — the app's data layer, replacing supabase-js→PostgREST.
 *
 * The frontend now talks to OUR backend: every query goes to
 * /api/v1/data/{table} on FastAPI, which runs it under Postgres RLS with the
 * caller's native JWT. supabase-js is gone.
 *
 * The API deliberately mirrors the supabase-js chain the app already uses, so
 * the 121 call sites keep their syntax — only the client underneath changes:
 *
 *   const { data, error } = await supabase
 *     .from('crm_deals')
 *     .select('title,stage,value')
 *     .eq('stage', 'closed_won')
 *     .order('value', { ascending: false })
 *     .limit(5);
 *
 * Supported chain methods (the subset the app actually uses):
 *   select / insert / update / delete / upsert / eq / neq / gt / gte / lt /
 *   lte / in / not.in / is / like / ilike / match / contains / order / limit /
 *   range / single / maybeSingle / returns
 *
 * The result of the chain is AWAITABLE and resolves to `{ data, error }` —
 * the exact shape the app destructures. Chain methods return the same
 * thenable, so `.eq().single()` works. Under the hood it builds a request
 * against our backend and sends the native JWT.
 */

import { api, apiConfigured } from './apiClient';

export interface DataResult<T> {
  // `data` is permissively typed so existing `data as Foo[]` casts keep
  // working — supabase-js's data was effectively `any` at these call sites.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentional, mirrors supabase-js
  data: T | any;
  error: { message: string; code?: string; details?: string; hint?: string } | null;
  /** Total matching rows when `.count()` was used (supabase-js returns this). */
  count: number | null;
  /** HTTP status (pipelineStages reads res.status for retry decisions). */
  status?: number;
}

type FilterValue = string | number | boolean | null | undefined | string[];

/**
 * What awaiting a query gives back: supabase-js's result types, so code
 * written against supabase-js type-checks the same against this replacement.
 *
 * ⚠️ 2026-10-18, own-stack integration. `data` was `T | any` here - effectively
 * `any` - and code written on main against supabase-js's types stopped
 * compiling: `events.map(event => event.id)` on an `any` gives `event` no type,
 * and CRA's build fails on TS7006 (5 sites: usePersonalEvents, mailTemplates,
 * mailSignatures; ThemeContext had the same on onAuthStateChange in
 * supabase.ts). supabase-js without a generated schema answers `any[] | null`
 * for a list and `any` for `.single()` / `.maybeSingle()`, and that is what this
 * now says. `.rpc()` keeps `DataResult` (data `any`), as supabase-js's rpc did.
 */
export interface QueryResult<R> extends Omit<DataResult<R>, 'data'> {
  data: R | null;
}

/** The chainable query builder. Thenable: await it for `{ data, error }`. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- supabase-js's untyped row
export interface QueryBuilder<T = any, R = T[]> extends PromiseLike<QueryResult<R>> {
  select(columns?: string, opts?: { count?: 'exact' | 'planned'; head?: boolean }): QueryBuilder<T, R>;
  insert(payload: object | object[]): QueryBuilder<T, R>;
  update(payload: object): QueryBuilder<T, R>;
  delete(): QueryBuilder<T, R>;
  upsert(payload: object | object[], opts?: { onConflict?: string; ignoreDuplicates?: boolean }): QueryBuilder<T, R>;
  eq(col: string, val: FilterValue): QueryBuilder<T, R>;
  or(spec: string): QueryBuilder<T, R>;
  neq(col: string, val: FilterValue): QueryBuilder<T, R>;
  gt(col: string, val: FilterValue): QueryBuilder<T, R>;
  gte(col: string, val: FilterValue): QueryBuilder<T, R>;
  lt(col: string, val: FilterValue): QueryBuilder<T, R>;
  lte(col: string, val: FilterValue): QueryBuilder<T, R>;
  in(col: string, vals: FilterValue[]): QueryBuilder<T, R>;
  not(col: string, op: string, val: FilterValue): QueryBuilder<T, R>;
  is(col: string, val: FilterValue): QueryBuilder<T, R>;
  like(col: string, val: string): QueryBuilder<T, R>;
  ilike(col: string, val: string): QueryBuilder<T, R>;
  match(obj: Record<string, FilterValue>): QueryBuilder<T, R>;
  contains(col: string, val: unknown): QueryBuilder<T, R>;
  order(col: string, opts?: { ascending?: boolean; nullsFirst?: boolean }): QueryBuilder<T, R>;
  limit(n: number): QueryBuilder<T, R>;
  range(from: number, to: number): QueryBuilder<T, R>;
  single(): QueryBuilder<T, T>;
  maybeSingle(): QueryBuilder<T, T>;
  returns<U = R>(): QueryBuilder<T, U>;
  head(): QueryBuilder<T, R>;
  count(opts?: { head?: boolean; count?: 'exact' | 'planned' }): QueryBuilder<T, R>;
  aborted(): QueryBuilder<T, R>;
  /** The selected columns — used by `.count()` / `.head()` where the client
   *  only cares about the total. The backend returns `{ count }`. */
  countSpec?: boolean;
}

/** Normalise a backend payload to a row array (insert returns one object; update/delete return arrays). */
function toRows(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw === null || raw === undefined) return [];
  return [raw];
}

/**
 * The `error` a failed request resolves to: the backend's message, plus its
 * `code` when it sent one. Callers branch on `error.code` the way they did with
 * supabase-js — `PGRST116` ("`.single()` found no row") above all. This kept the
 * message only until 2026-10-17, so every such branch saw `undefined` and threw.
 * No code from the backend means no `code` key here, never a guessed one.
 */
function failureOf(
  res: { error: string; code?: string },
  fallback = '',
): NonNullable<DataResult<unknown>['error']> {
  const message = res.error || fallback;
  return res.code ? { message, code: res.code } : { message };
}

/**
 * A filter value, RAW. Never percent-encode it here.
 *
 * `filtersToQuery()` puts every filter through `URLSearchParams`, which encodes
 * each value exactly once, and the backend decodes it exactly once. Encoding it
 * here as well (as this did until 2026-10-17) sent every value encoded TWICE, so
 * the backend received the encoded text itself: `.lt('activity_date', nowIso)`
 * reached Postgres as `2026-10-17T13%3A39...` and failed as an invalid
 * timestamp, `.eq('email', 'a@b.test')` compared against `a%40b.test` and
 * matched nothing, and `.ilike('name', '%nord%')` searched for `%25nord%25`.
 */
function fmt(val: FilterValue): string {
  if (val === null) return 'null';
  if (Array.isArray(val)) return `(${val.map(v => String(v)).join(',')})`;
  return String(val);
}

class QueryBuilderImpl<T, R = T[]> implements QueryBuilder<T, R> {
  table: string;
  op: 'select' | 'insert' | 'update' | 'delete' | 'upsert' = 'select';
  selectSpec = '*';
  body: unknown = undefined;
  filters: Array<[string, string]> = [];
  orderSpec: string[] = [];
  limitSpec: number | null = null;
  offsetSpec: number | null = null;
  singleSpec = false;
  /** `.maybeSingle()` — like single(), but "no row" is an empty result, not an error. */
  maybeSingleSpec = false;
  /** `.select()` was chained onto a write: return the written rows. */
  wantsReturn = false;
  upsertConflict: string | null = null;
  /** `.upsert(x, { ignoreDuplicates: true })` — keep the existing row on conflict. */
  upsertIgnoreDuplicates = false;
  headSpec = false;
  countSpec = false;

  constructor(table: string) {
    this.table = table;
  }

  // ── Chain methods ──────────────────────────────────────────────────────────
  select(columns?: string, opts?: { count?: 'exact' | 'planned'; head?: boolean }): QueryBuilder<T, R> {
    if (columns) this.selectSpec = columns;
    // supabase-js's write-and-return idiom is `.insert(x).select()` — `select()`
    // always runs AFTER the write method, and it means "perform the write, then
    // give me back the written rows". It must NOT downgrade the pending write to
    // a read: doing so sends a GET, the write never reaches the database, and the
    // caller sees a success with no error. Only promote to a read when no write
    // is pending.
    if (this.op === 'select') {
      this.op = 'select';
    } else {
      this.wantsReturn = true;
    }
    if (opts?.count) this.countSpec = true;
    if (opts?.head) this.headSpec = true;
    return this;
  }
  insert(payload: object | object[]): QueryBuilder<T, R> {
    this.op = 'insert'; this.body = payload; return this;
  }
  update(payload: object): QueryBuilder<T, R> {
    this.op = 'update'; this.body = payload; return this;
  }
  delete(): QueryBuilder<T, R> { this.op = 'delete'; return this; }
  upsert(payload: object | object[], opts?: { onConflict?: string; ignoreDuplicates?: boolean }): QueryBuilder<T, R> {
    this.op = 'upsert'; this.body = payload;
    if (opts?.onConflict) this.upsertConflict = opts.onConflict;
    // `ignoreDuplicates` is its own flag. It used to be smuggled through as a
    // '__none__' conflict target, which the backend then treated as a column
    // name — and it silently lost the flag entirely whenever onConflict was
    // also given, which is exactly how the notification de-duplication is written.
    if (opts?.ignoreDuplicates) this.upsertIgnoreDuplicates = true;
    return this;
  }
  eq(col: string, val: FilterValue): QueryBuilder<T, R> { this.filters.push([col, `eq.${fmt(val)}`]); return this; }
  or(spec: string): QueryBuilder<T, R> { this.filters.push(['_or', spec]); return this; }
  neq(col: string, val: FilterValue): QueryBuilder<T, R> { this.filters.push([col, `neq.${fmt(val)}`]); return this; }
  gt(col: string, val: FilterValue): QueryBuilder<T, R> { this.filters.push([col, `gt.${fmt(val)}`]); return this; }
  gte(col: string, val: FilterValue): QueryBuilder<T, R> { this.filters.push([col, `gte.${fmt(val)}`]); return this; }
  lt(col: string, val: FilterValue): QueryBuilder<T, R> { this.filters.push([col, `lt.${fmt(val)}`]); return this; }
  lte(col: string, val: FilterValue): QueryBuilder<T, R> { this.filters.push([col, `lte.${fmt(val)}`]); return this; }
  in(col: string, vals: FilterValue[]): QueryBuilder<T, R> { this.filters.push([col, `in.(${vals.map(v => String(v)).join(',')})`]); return this; }
  not(col: string, op: string, val: FilterValue): QueryBuilder<T, R> { this.filters.push([col, `not.${op}.${fmt(val)}`]); return this; }
  is(col: string, val: FilterValue): QueryBuilder<T, R> { this.filters.push([col, `is.${fmt(val)}`]); return this; }
  // Raw for the same reason as fmt(): URLSearchParams encodes once.
  like(col: string, val: string): QueryBuilder<T, R> { this.filters.push([col, `like.${val}`]); return this; }
  ilike(col: string, val: string): QueryBuilder<T, R> { this.filters.push([col, `ilike.${val}`]); return this; }
  match(obj: Record<string, FilterValue>): QueryBuilder<T, R> {
    for (const [c, v] of Object.entries(obj)) this.filters.push([c, `eq.${fmt(v)}`]);
    return this;
  }
  contains(col: string, val: unknown): QueryBuilder<T, R> {
    this.filters.push([col, `contains.${JSON.stringify(val)}`]);
    return this;
  }
  order(col: string, opts?: { ascending?: boolean; nullsFirst?: boolean }): QueryBuilder<T, R> {
    const dir = opts?.ascending === false ? 'desc' : 'asc';
    this.orderSpec.push(`${col}.${dir}`);
    return this;
  }
  limit(n: number): QueryBuilder<T, R> { this.limitSpec = n; return this; }
  range(from: number, to: number): QueryBuilder<T, R> { this.limitSpec = to - from + 1; this.offsetSpec = from; return this; }
  single(): QueryBuilder<T, T> {
    this.singleSpec = true;
    return this as unknown as QueryBuilder<T, T>;
  }
  // supabase-js: maybeSingle() returns `{ data: null, error: null }` when nothing
  // matches, where single() returns an error. Treating them alike turned every
  // "not created yet" check into a hard failure, so it gets its own flag and does
  // NOT send `single=true` (which makes the backend answer 406 / PGRST116 on an
  // empty result).
  maybeSingle(): QueryBuilder<T, T> {
    this.maybeSingleSpec = true;
    return this as unknown as QueryBuilder<T, T>;
  }
  returns<U = R>(): QueryBuilder<T, U> { return this as unknown as QueryBuilder<T, U>; }
  head(): QueryBuilder<T, R> { this.headSpec = true; return this; }
  count(opts?: { head?: boolean; count?: 'exact' | 'planned' }): QueryBuilder<T, R> {
    this.countSpec = true;
    if (opts?.head) this.headSpec = true;
    return this;
  }
  aborted(): QueryBuilder<T, R> { return this; }

  // ── Execution (thenable) ───────────────────────────────────────────────────
  private async execute(): Promise<DataResult<T>> {
    // Self-profile reads must work before the workspace is known (during
    // login the profile fetch SETS the workspace — a chicken-and-egg the
    // workspace gate would deadlock). Allow the profiles table through on the
    // auth base, which is workspace-independent.
    const isProfile = this.table === 'profiles';
    if (!isProfile && !apiConfigured()) {
      return { data: null, count: null, error: { message: 'Backend not configured' } };
    }
    try {
      if (this.op === 'insert') {
        const res = await api.post<unknown>(`/data/${this.table}`, this.body);
        if (!res.ok) return { data: null, count: null, error: failureOf(res) };
        return this.shapeWrite(res.data);
      }
      if (this.op === 'update') {
        const qs = this.filtersToQuery();
        const res = await api.patch<unknown>(`/data/${this.table}${qs}`, this.body);
        if (!res.ok) return { data: null, count: null, error: failureOf(res) };
        return this.shapeWrite(res.data);
      }
      if (this.op === 'delete') {
        const qs = this.filtersToQuery();
        const res = await api.delete<unknown>(`/data/${this.table}${qs}`);
        if (!res.ok) return { data: null, count: null, error: failureOf(res) };
        return this.shapeWrite(res.data);
      }
      if (this.op === 'upsert') {
        const up = new URLSearchParams();
        if (this.upsertConflict) up.set('on_conflict', this.upsertConflict);
        if (this.upsertIgnoreDuplicates) up.set('ignore_duplicates', 'true');
        const qs = up.toString() ? `?${up.toString()}` : '';
        const res = await api.post<unknown>(`/data/${this.table}/upsert${qs}`, this.body);
        if (!res.ok) return { data: null, count: null, error: failureOf(res) };
        return this.shapeWrite(res.data);
      }
      // select
      const qs = this.filtersToQuery();
      const res = await api.get<unknown>(`/data/${this.table}${qs}`);
      if (!res.ok) return { data: null, count: null, error: failureOf(res) };
      if (this.countSpec || this.headSpec) {
        const payload = res.data as { count?: number; data?: unknown[] };
        return {
          // supabase-js: .count() with head => data is null; with count=exact
          // the rows come back under data. We return both.
          data: (this.headSpec ? null : payload.data) as T,
          count: payload.count ?? 0,
          error: null,
        };
      }
      if (this.maybeSingleSpec) {
        const rows = toRows(res.data);
        return { data: (rows.length > 0 ? rows[0] : null) as T, count: null, error: null };
      }
      return { data: res.data as T, count: null, error: null };
    } catch (err) {
      return {
        data: null,
        count: null,
        error: { message: err instanceof Error ? err.message : 'Data request failed' },
      };
    }
  }

  /**
   * Shape a write's response the way supabase-js does.
   *
   * Without a chained `.select()` the backend's payload is passed through
   * unchanged — that is what callers already relied on, so it stays as it was.
   * With `.select()` the written rows come back as an ARRAY, or as a single
   * object when `.single()` / `.maybeSingle()` was chained. `.single()` on an
   * empty result is an error carrying PGRST116 (the code supabase-js used, and
   * the one the app's own branches test for); `.maybeSingle()` yields null.
   */
  private shapeWrite(raw: unknown): DataResult<T> {
    if (!this.wantsReturn) return { data: raw as T, count: null, error: null };
    const rows = toRows(raw);
    if (this.singleSpec || this.maybeSingleSpec) {
      if (rows.length === 0) {
        return this.maybeSingleSpec
          ? { data: null, count: null, error: null }
          : {
              data: null,
              count: null,
              error: {
                message: 'JSON object requested, multiple (or no) rows returned',
                code: 'PGRST116',
              },
            };
      }
      return { data: rows[0] as T, count: null, error: null };
    }
    return { data: rows as T, count: null, error: null };
  }

  private filtersToQuery(): string {
    const params = new URLSearchParams();
    if (this.selectSpec && this.selectSpec !== '*') params.set('select', this.selectSpec);
    // append, NOT set: a column legitimately carries two filters —
    // `.gte('date', from).lte('date', to)` is how every date range in the app is
    // written. `set` overwrote the first, so ranges silently lost their lower
    // bound and reports returned everything before the end date.
    for (const [col, val] of this.filters) params.append(col, val);
    if (this.orderSpec.length > 0) params.set('order', this.orderSpec.join(','));
    if (this.limitSpec !== null) params.set('limit', String(this.limitSpec));
    if (this.offsetSpec !== null) params.set('offset', String(this.offsetSpec));
    if (this.singleSpec) params.set('single', 'true');
    if (this.countSpec) params.set('count', 'exact');
    if (this.headSpec) params.set('head', 'true');
    const s = params.toString();
    return s ? `?${s}` : '';
  }

  then<TResult1 = QueryResult<R>, TResult2 = never>(
    onfulfilled?: ((value: QueryResult<R>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): Promise<TResult1 | TResult2> {
    // execute() builds the shapes at run time; R only names them for the caller.
    return (this.execute() as Promise<QueryResult<R>>).then(onfulfilled, onrejected);
  }
}

/**
 * The drop-in client. Replaces `createClient`/`supabase.from(...)` with our
 * own backend data layer. Only `from()` is needed by the app (auth is native).
 */
export const dataClient = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- supabase-js's untyped row
  from<T = any>(table: string): QueryBuilder<T> {
    return new QueryBuilderImpl<T>(table);
  },
  /**
   * Call a stored function (RPC) through our backend, under the caller's RLS
   * identity. Returns `{ data, error }` — the shape the app destructures.
   */
  async rpc<T = Record<string, unknown>>(
    fn: string,
    params?: Record<string, unknown>,
  ): Promise<DataResult<T>> {
    if (!apiConfigured()) {
      return { data: null, count: null, error: { message: 'Backend not configured' } };
    }
    try {
      const res = await api.post<unknown>(`/data/rpc/${fn}`, params ?? {});
      if (!res.ok) {
        // FastAPI surfaces the RAISE message; keep it as the error.
        return { data: null, count: null, error: failureOf(res, 'RPC failed') };
      }
      return { data: res.data as T, count: null, error: null };
    } catch (err) {
      return {
        data: null,
        count: null,
        error: { message: err instanceof Error ? err.message : 'RPC failed' },
      };
    }
  },
};
